import {
  Analytics,
  FavouriteBtn,
  HelmetCanonical,
  useAnalyticsUtil,
  useInitialFocus,
} from "@skv/core";
import { Remark } from "@skv/react-remark";
import { opportunityStore, useAppSettings } from "@skv/state";
import { FAVOURITES_ENUM } from "@skv/state/src/state/favourites";
import * as React from "react";
import { Helmet } from "react-helmet";
import { Link, useLocation, useParams, useRouteMatch } from "react-router-dom";
import { ThemeProvider } from "styled-components";
import { ReportOpportunityNSW } from "~/ui";
import { ValidationError } from "~/ui/Form";

/*
  By time we hit this components, opportunity.data already exists,
  as the parent OpportunityDetails page handles loading
*/

interface OpportunityDetailsMainProps {
  focusApply?: boolean;
}

const OpportunityDetailsMain = ({
  focusApply = false,
}: OpportunityDetailsMainProps): JSX.Element => {
  const { applicationName, applicationUrl } = useAppSettings();

  const applyButtonRef = useInitialFocus<HTMLAnchorElement>({
    shouldFocus: focusApply,
  });

  const match = useRouteMatch();
  const params = useParams<{ id: string }>();
  const location = useLocation();

  const { trackPageLink } = useAnalyticsUtil();

  const { data } = opportunityStore.hooks.useOpportunity(params.id);
  const { id, name, url, description, organisation } = data;

  const handleApplyLinkClick = React.useCallback(
    (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      trackPageLink("express-of-interest");
    },
    [trackPageLink]
  );

  return (
    <>
      <Helmet>
        <title>{`${name}, ${organisation.name}`}</title>
        <meta
          name="description"
          content={`Volunteer at ${name} - ${organisation.category} with ${organisation.name}. Enquire now at ${applicationName}`}
        />
        <meta property="og:title" content={`${name}, ${organisation.name}`} />
        <meta
          property="og:description"
          content={`Volunteer at ${name} - ${organisation.category} with ${organisation.name}. Enquire now at ${applicationName}`}
        />
      </Helmet>

      <HelmetCanonical url={`${applicationUrl}${url}`} />

      <div className="text-break mb-4">
        <Remark
          rehypeReactOptions={{
            components: {
              a: (props) => {
                return <>{props.children}</>;
              },
              code: (props) => {
                return <>{props.children}</>;
              },
              pre: (props) => {
                return <p>{props.children}</p>;
              },
            },
          }}
        >
          {description}
        </Remark>
      </div>

      <div className="row no-gutters align-items-center mb-5">
        <div className="col-auto">
          {data.acceptsApplication ? (
            <Link
              ref={applyButtonRef}
              className="btn btn-primary"
              css={`
                padding-left: 2.5rem;
                padding-right: 2.5rem;
              `}
              to={{
                pathname: `${match.url}/apply`,
                state: {
                  ...location.state,
                  referer: "/opportunity-details",
                },
              }}
              onClick={handleApplyLinkClick}
            >
              Apply
            </Link>
          ) : (
            <button
              type="button"
              className="btn btn-light"
              css={`
                padding-left: 2.5rem;
                padding-right: 2.5rem;

                &:disabled {
                  background-color: ${(p) => p.theme.gray300};
                }
              `}
              disabled
            >
              Apply
            </button>
          )}
        </div>
        {data.acceptsApplication && (
          <div className="col-auto ml-sm-auto">
            <ThemeProvider
              theme={(theme) => ({
                ...theme,
                dangerColor: theme.primaryColor,
              })}
            >
              <FavouriteBtn id={id} type={FAVOURITES_ENUM.OPPORTUNITY} />
            </ThemeProvider>
          </div>
        )}
      </div>
      {!data.acceptsApplication && (
        <ValidationError
          errorId="application-error-message"
          message="You can not apply for this role as it is no longer available."
        />
      )}

      {data.reportOpportunityForm && (
        <ReportOpportunityNSW {...data.reportOpportunityForm} />
      )}

      <Analytics
        data={{
          subSection: "opportunity",
          subSubSection: "opportunity:details",
          subSubSubSection: "opportunity:details",
          pageTitle: "opportunity:details",
          pageType: "opportunity:details",
          organisationId: organisation.id,
          opportunityId: id,
        }}
      />
    </>
  );
};

export default OpportunityDetailsMain;
